import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap, of, switchMap, withLatestFrom} from 'rxjs';
import {DEVICE_STATE_SERVICE_INJECTION_TOKEN, DeviceStateService} from '../../../core/services/api/device-state-service.interface';
import {selectDeviceFilter} from './device.selectors';
import {Store} from '@ngrx/store';
import {DeviceActions} from './device.actions';
import {DeviceResponseDto, DeviceUpdateRequest} from '../models/device-response.interface';
import {DeviceGroupActions} from './device-groups/device-group.actions';

@Injectable()
export class DeviceEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private deviceStateService = inject<DeviceStateService>(DEVICE_STATE_SERVICE_INJECTION_TOKEN);


  public updateFilter$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceActions.public.updateDeviceFilter),
      map((_props) => DeviceActions.private.loadDevicesWithFilters())
    );
  });

  public loadDevices$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceActions.private.loadDevicesWithFilters),
      withLatestFrom(this.store.select(selectDeviceFilter)),
      mergeMap(([_action, filter]) => {
        return this.deviceStateService.getDevices(filter).pipe(
          map((devicesResponse) => DeviceActions.private.loadDevicesSuccess({devicesResponseDto: devicesResponse})),
          catchError((error) => of(DeviceActions.private.loadDevicesFailure({error})))
        )
      }),
    );
  });

  public loadDevicesWithProvidedFilters$ = createEffect(() => this.actions$.pipe(
      ofType(DeviceActions.private.loadDevicesWithProvidedFilters),
      switchMap((props) => this.deviceStateService.getDevices(
        props.filter,
      ).pipe(
        map((responseDto) => DeviceActions.private.loadDevicesWithProvidedFiltersSuccess({
          devicesResponseDto: responseDto,
        })),
        catchError((error) => of(DeviceActions.private.loadDevicesWithProvidedFiltersFailure({
          error: error,
        }))),
      )),
    )
  );

  public loadAllDevicesByFilters$ = createEffect(() => this.actions$.pipe(
      ofType(DeviceActions.public.loadAllDevicesByFilters),
      map((props) => DeviceActions.private.loadNextOfAll({page: 0, filter: props.filter ?? {}})),
      catchError((error) => of(DeviceActions.private.loadAllDevicesByFiltersFailure({
        error: error,
      }))),
    )
  );

  public loadNextOfAll$ = createEffect(() => this.actions$.pipe(
      ofType(DeviceActions.private.loadNextOfAll),
      switchMap((props) => {
          const pageSize = 50;
          return this.deviceStateService.getDevices(
            {...props.filter, pageSize: pageSize, page: props.page},
          ).pipe(
            map((responseDto) => {
              this.store.dispatch(DeviceActions.private.loadNextOfAllSuccess({
                devicesResponseDto: responseDto,
              }))

              if ((props.page + 1) * pageSize >= responseDto.totalCount) {
                return DeviceActions.private.loadAllDevicesByFiltersSuccess()
              }

              return DeviceActions.private.loadNextOfAll({page: props.page + 1, filter: props.filter})
            }),
            catchError((error) => of(
              DeviceActions.private.loadNextOfAllFailure({error}),
              DeviceActions.private.loadAllDevicesByFiltersFailure({error})
            )),
          )
        }
      ),
    )
  );

  public triggerDeviceAction$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceActions.public.triggerDeviceAction),
    switchMap((props) => {
      return this.deviceStateService.triggerDeviceAction({
        id: props.id,
        value: props.value,
      }).pipe(
        mergeMap((responseDto) => of(
          DeviceActions.private.triggerDeviceActionSuccess({
            responseDto: responseDto,
          }),
          DeviceGroupActions.private.loadDeviceGroupsWithProvidedFilters({
            filter: {
              deviceId: responseDto.id,
            },
          }),
        )),
        catchError((error) => of(DeviceActions.private.triggerDeviceActionFailure({
          id: props.id,
          attemptedValue: props.value,
          error: error,
        }))),
      );
    }),
  ));

  public updateDevice$ = createEffect(() => this.actions$.pipe(
    ofType(DeviceActions.public.updateDevice),
    switchMap((props) => {
      const {type: _actionType, deviceType, ...updatePayload} = props;
      const update: DeviceUpdateRequest = {
        ...updatePayload,
        ...(deviceType !== undefined ? {type: deviceType} : {}),
      };

      return this.deviceStateService.updateDevice(update).pipe(
        map((responseDto) => DeviceActions.private.updateDeviceSuccess({
          responseDto: responseDto,
        })),
        catchError((error) => of(DeviceActions.private.updateDeviceFailure({
          id: props.id,
          error: error,
        }))),
      );
    }),
  ));

  public loadDeviceById$ = createEffect(() => this.actions$.pipe(
      ofType(DeviceActions.public.loadDeviceById),
      switchMap((props) => this.deviceStateService.getDeviceById({
          id: props.id,
        }).pipe(
          map((responseDto: DeviceResponseDto) => DeviceActions.private.loadDeviceByIdSuccess({
            responseDto: responseDto,
          })),
          catchError((error) => of(DeviceActions.private.loadDeviceByIdFailure({
            id: props.id,
            error: error,
          }))),
        )
      ),
    ),
    {dispatch: true}
  );

  public refreshDeviceGroupData$ = createEffect(() => this.actions$.pipe(
      ofType(DeviceActions.public.refreshDeviceDataFromWebSocket),
      map((props) => DeviceGroupActions.private.loadDeviceGroupsWithProvidedFilters({
        filter: {
          deviceId: props.device.id,
        },
      })),
    ),
    {dispatch: true}
  );
}
